import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Editorial full-screen lightbox.
 * - Backdrop blur, ESC/Arrow keys, prev/next buttons, image counter.
 * - Pass `openIdx` = -1 to close.
 * - `objectFit` supports "contain" (full image) or "cover".
 */
export const Lightbox = ({
  images = [],
  openIdx,
  onClose,
  onNav,
  objectFit = "contain",
  testId = "lightbox",
}) => {
  const open = openIdx >= 0 && images.length > 0;

  useEffect(() => {
    if (!open) return undefined;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav((openIdx + 1) % images.length);
      if (e.key === "ArrowLeft") onNav((openIdx - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, openIdx, images.length, onClose, onNav]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="lb"
          data-testid={testId}
          className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-10 sm:p-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-black/85 backdrop-blur-md" />

          <button
            type="button"
            data-testid={`${testId}-close`}
            aria-label="Close"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="absolute top-5 right-5 sm:top-8 sm:right-8 z-10 w-11 h-11 flex items-center justify-center border border-white/25 text-white hover:bg-white hover:text-black transition-colors"
          >
            <X size={18} />
          </button>

          {images.length > 1 && (
            <>
              <button
                type="button"
                aria-label="Previous"
                onClick={(e) => { e.stopPropagation(); onNav((openIdx - 1 + images.length) % images.length); }}
                className="absolute left-3 sm:left-8 z-10 w-11 h-11 flex items-center justify-center border border-white/25 text-white hover:bg-white hover:text-black transition-colors"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                type="button"
                aria-label="Next"
                onClick={(e) => { e.stopPropagation(); onNav((openIdx + 1) % images.length); }}
                className="absolute right-3 sm:right-8 z-10 w-11 h-11 flex items-center justify-center border border-white/25 text-white hover:bg-white hover:text-black transition-colors"
              >
                <ChevronRight size={20} />
              </button>
            </>
          )}

          <motion.figure
            key={openIdx}
            className="relative max-w-6xl w-full max-h-[85vh] flex flex-col items-center"
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={typeof images[openIdx] === "string" ? images[openIdx] : images[openIdx].url}
              alt={typeof images[openIdx] === "string" ? "" : (images[openIdx].alt || "")}
              className={`max-h-[80vh] w-auto max-w-full shadow-[0_30px_80px_rgba(0,0,0,0.5)] object-${objectFit}`}
            />
            <figcaption className="mt-4 font-serif italic text-sm sm:text-base text-white/70 text-center">
              {openIdx + 1} / {images.length}
            </figcaption>
          </motion.figure>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
